import { Fragment, useEffect, useState } from "react";
import { auth } from "@/lib/firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type User,
} from "firebase/auth";
import {
  suscribirInscripciones,
  cambiarEstadoInscripcion,
  eliminarInscripcion,
  suscribirInscripcionesAbiertas,
  setInscripcionesAbiertas,
  type Inscripcion,
  type EstadoInscripcion,
} from "@/lib/inscripciones";
import { DISTANCIAS, CATEGORIAS } from "@/lib/categorias";

const BADGE: Record<EstadoInscripcion, string> = {
  pendiente: "bg-yellow-100 text-yellow-800",
  aprobado: "bg-green-100 text-green-800",
  negado: "bg-red-100 text-red-800",
};

export default function AdminPage() {
  const [user, setUser] = useState<User | null>(null);
  const [cargandoAuth, setCargandoAuth] = useState(true);

  // login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // datos
  const [inscripciones, setInscripciones] = useState<Inscripcion[]>([]);
  const [cargandoDatos, setCargandoDatos] = useState(true);

  // filtros
  const [filtro, setFiltro] = useState<"todos" | EstadoInscripcion>("todos");
  const [fDistancia, setFDistancia] = useState("todas");
  const [fCategoria, setFCategoria] = useState("todas");
  const [fSexo, setFSexo] = useState("todos");
  const [busqueda, setBusqueda] = useState("");

  // id de la fila cuyo comprobante está desplegado (toggle)
  const [filaAbierta, setFilaAbierta] = useState<string | null>(null);

  // eliminación con confirmación por contraseña
  const [aEliminar, setAEliminar] = useState<Inscripcion | null>(null);
  const [passEliminar, setPassEliminar] = useState("");
  const [errorEliminar, setErrorEliminar] = useState("");
  const [eliminando, setEliminando] = useState(false);

  // inscripciones abiertas/cerradas
  const [abiertas, setAbiertas] = useState(true);
  const [cambiandoAbiertas, setCambiandoAbiertas] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setCargandoAuth(false);
    });
  }, []);

  useEffect(() => suscribirInscripcionesAbiertas(setAbiertas), []);

  async function alternarAbiertas() {
    setCambiandoAbiertas(true);
    try {
      await setInscripcionesAbiertas(!abiertas);
    } catch (err) {
      console.error(err);
      alert("No se pudo cambiar el estado de las inscripciones.");
    } finally {
      setCambiandoAbiertas(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    setCargandoDatos(true);
    const cancelar = suscribirInscripciones(
      (lista) => {
        setInscripciones(lista);
        setCargandoDatos(false);
      },
      (err) => {
        console.error(err);
        setCargandoDatos(false);
      }
    );
    return cancelar;
  }, [user]);

  async function onLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoginError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setEmail("");
      setPassword("");
    } catch (err) {
      console.error(err);
      setLoginError("Credenciales incorrectas o usuario inexistente.");
    }
  }

  async function setEstado(id: string, estado: EstadoInscripcion, cedula: string) {
    try {
      await cambiarEstadoInscripcion(id, estado, cedula);
    } catch (err) {
      console.error(err);
      alert("No se pudo actualizar el estado.");
    }
  }

  function abrirEliminar(i: Inscripcion) {
    setAEliminar(i);
    setPassEliminar("");
    setErrorEliminar("");
  }

  async function confirmarEliminar(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!aEliminar || !user?.email) return;
    setErrorEliminar("");
    setEliminando(true);
    try {
      // Doble verificación: re-autenticar con la contraseña del admin.
      const cred = EmailAuthProvider.credential(user.email, passEliminar);
      await reauthenticateWithCredential(user, cred);
      // Si la contraseña es correcta, eliminar.
      await eliminarInscripcion(aEliminar.id, aEliminar.cedula);
      setAEliminar(null);
      setPassEliminar("");
    } catch (err) {
      console.error(err);
      setErrorEliminar("Contraseña incorrecta o no se pudo eliminar. Intenta de nuevo.");
    } finally {
      setEliminando(false);
    }
  }

  function verComprobante(i: Inscripcion) {
    if (!i.comprobanteUrl) {
      alert("Esta inscripción no tiene comprobante adjunto.");
      return;
    }
    // Si ya estaba abierta esta fila, la cierra; si no, la abre.
    setFilaAbierta((actual) => (actual === i.id ? null : i.id));
  }

  // Número de inscrito (orden de inscripción ascendente: el más antiguo es el 1).
  // Se mantiene aunque se apliquen filtros.
  const numeroPorId = new Map<string, number>();
  [...inscripciones].reverse().forEach((i, idx) => numeroPorId.set(i.id, idx + 1));

  // Lista con todos los filtros aplicados.
  const termino = busqueda.trim().toLowerCase();
  const lista = inscripciones.filter((i) => {
    if (filtro !== "todos" && i.estado !== filtro) return false;
    if (fDistancia !== "todas" && i.distancia !== fDistancia) return false;
    if (fCategoria !== "todas" && i.categoria !== fCategoria) return false;
    if (fSexo !== "todos" && i.sexo !== fSexo) return false;
    if (termino && !`${i.nombres} ${i.cedula} ${i.email}`.toLowerCase().includes(termino)) return false;
    return true;
  });

  // Descarga la lista filtrada como CSV (se abre en Excel) con el estado incluido.
  function exportarExcel() {
    const headers = [
      "N°", "Estado", "Nombres", "Cédula", "Fecha Nac.", "Distancia", "Categoría",
      "Sexo", "Teléfono", "Email", "Club/Provincia", "Talla", "Comentario", "Comprobante (URL)",
    ];
    const filas = lista.map((i) => [
      numeroPorId.get(i.id) ?? "", i.estado, i.nombres, i.cedula, i.fechaNacimiento,
      i.distancia, i.categoria, i.sexo, i.telefono, i.email, i.clubProvincia,
      i.tallaCamiseta, i.comentario ?? "", i.comprobanteUrl ?? "",
    ]);
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...filas].map((f) => f.map(esc).join(";")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inscripciones-banosrun-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (cargandoAuth) {
    return <div className="min-h-screen flex items-center justify-center text-foreground-600">Cargando…</div>;
  }

  // ---------- Pantalla de login ----------
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow">
          <h1 className="font-heading text-2xl text-foreground-950 mb-6">ADMINISTRACIÓN</h1>
          {loginError && (
            <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-800 text-sm">{loginError}</div>
          )}
          <form onSubmit={onLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-1">Correo</label>
              <input
                className="w-full px-4 py-3 rounded-xl border border-foreground-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-1">Contraseña</label>
              <input
                className="w-full px-4 py-3 rounded-xl border border-foreground-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors"
            >
              ENTRAR
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- Panel ----------
  return (
    <div className="min-h-screen bg-background-50 px-3 md:px-6 py-8">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">INSCRIPCIONES</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-foreground-600 hidden md:inline">{user.email}</span>
            <button
              onClick={() => signOut(auth)}
              className="px-4 py-2 rounded-full bg-foreground-200 text-foreground-800 text-sm font-semibold hover:bg-foreground-300"
            >
              Cerrar sesión
            </button>
          </div>
        </div>

        {/* Toggle abrir / cerrar inscripciones */}
        <div
          className={`flex flex-wrap items-center justify-between gap-3 mb-4 p-4 rounded-2xl border ${
            abiertas ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${abiertas ? "bg-green-500" : "bg-red-500"}`} />
            <span className="text-sm font-semibold text-foreground-800">
              Inscripciones: {abiertas ? "ABIERTAS" : "CERRADAS"}
            </span>
            <span className="text-sm text-foreground-500 hidden sm:inline">
              {abiertas
                ? "— el formulario público está activo."
                : '— el público ve "Inscripciones terminadas".'}
            </span>
          </div>
          <button
            onClick={alternarAbiertas}
            disabled={cambiandoAbiertas}
            className={`px-4 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-60 ${
              abiertas ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {cambiandoAbiertas
              ? "Guardando…"
              : abiertas
              ? "Cerrar inscripciones"
              : "Abrir inscripciones"}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar nombre, cédula o email…"
            className="px-4 py-2 rounded-xl border border-foreground-200 bg-white text-sm flex-1 min-w-[180px]"
          />
          <select
            className="px-3 py-2 rounded-xl border border-foreground-200 bg-white text-sm"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as typeof filtro)}
          >
            <option value="todos">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="aprobado">Aprobados</option>
            <option value="negado">Negados</option>
          </select>
          <select
            className="px-3 py-2 rounded-xl border border-foreground-200 bg-white text-sm"
            value={fDistancia}
            onChange={(e) => setFDistancia(e.target.value)}
          >
            <option value="todas">Todas las distancias</option>
            {DISTANCIAS.map((d) => (
              <option key={d.nombre} value={d.nombre}>{d.nombre}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 rounded-xl border border-foreground-200 bg-white text-sm"
            value={fCategoria}
            onChange={(e) => setFCategoria(e.target.value)}
          >
            <option value="todas">Todas las categorías</option>
            {CATEGORIAS.map((c) => (
              <option key={c.nombre} value={c.nombre}>{c.nombre}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 rounded-xl border border-foreground-200 bg-white text-sm"
            value={fSexo}
            onChange={(e) => setFSexo(e.target.value)}
          >
            <option value="todos">Damas y Varones</option>
            <option value="Damas">Damas</option>
            <option value="Varones">Varones</option>
          </select>

          <span className="text-sm font-semibold text-foreground-600">
            {lista.length} inscrito(s)
          </span>

          <button
            onClick={exportarExcel}
            disabled={lista.length === 0}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
          >
            <i className="ri-file-excel-2-line" /> Descargar reporte
          </button>
        </div>

        <div className="overflow-x-auto bg-white rounded-2xl shadow">
          <table className="w-full text-xs lg:text-sm">
            <thead>
              <tr className="bg-foreground-50 text-left text-foreground-600">
                <th className="px-2 py-2">N°</th>
                <th className="px-2 py-2">Estado</th>
                <th className="px-2 py-2">Nombres</th>
                <th className="px-2 py-2">Cédula</th>
                <th className="px-2 py-2">F. Nac.</th>
                <th className="px-2 py-2">Distancia</th>
                <th className="px-2 py-2">Categoría</th>
                <th className="px-2 py-2">Sexo</th>
                <th className="px-2 py-2">Teléfono</th>
                <th className="px-2 py-2">Email</th>
                <th className="px-2 py-2">Club/Provincia</th>
                <th className="px-2 py-2">Talla</th>
                <th className="px-2 py-2">Comprobante</th>
                <th className="px-2 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargandoDatos ? (
                <tr>
                  <td colSpan={14} className="px-3 py-6 text-center text-foreground-500">Cargando…</td>
                </tr>
              ) : lista.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-3 py-6 text-center text-foreground-500">Sin registros.</td>
                </tr>
              ) : (
                lista.map((i) => (
                  <Fragment key={i.id}>
                    <tr className="border-t border-foreground-100 align-top">
                      <td className="px-2 py-2 font-semibold text-foreground-700">
                        {numeroPorId.get(i.id)}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${BADGE[i.estado]}`}>
                          {i.estado}
                        </span>
                      </td>
                      <td className="px-2 py-2">{i.nombres}</td>
                      <td className="px-2 py-2">{i.cedula}</td>
                      <td className="px-2 py-2">{i.fechaNacimiento}</td>
                      <td className="px-2 py-2">{i.distancia}</td>
                      <td className="px-2 py-2">{i.categoria}</td>
                      <td className="px-2 py-2">{i.sexo}</td>
                      <td className="px-2 py-2">{i.telefono}</td>
                      <td className="px-2 py-2 break-all">{i.email}</td>
                      <td className="px-2 py-2">{i.clubProvincia}</td>
                      <td className="px-2 py-2">{i.tallaCamiseta}</td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => verComprobante(i)}
                          className="px-3 py-1 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600"
                        >
                          {filaAbierta === i.id ? "Ocultar" : "Ver foto"}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            onClick={() => setEstado(i.id, "aprobado", i.cedula)}
                            disabled={i.estado === "aprobado"}
                            className="px-3 py-1 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-40"
                          >
                            Aprobar
                          </button>
                          <button
                            onClick={() => setEstado(i.id, "negado", i.cedula)}
                            disabled={i.estado === "negado"}
                            className="px-3 py-1 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-40"
                          >
                            Negar
                          </button>
                          <button
                            onClick={() => abrirEliminar(i)}
                            title="Eliminar inscripción"
                            className="px-3 py-1 rounded-lg bg-foreground-200 text-foreground-700 text-xs font-semibold hover:bg-foreground-300"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Fila expandible con el comprobante del usuario */}
                    {filaAbierta === i.id && (
                      <tr className="bg-foreground-50">
                        <td colSpan={14} className="px-3 py-4">
                          {i.comentario && (
                            <p className="mb-3 text-sm text-foreground-700">
                              <span className="font-semibold">Comentario:</span> {i.comentario}
                            </p>
                          )}
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-foreground-700">
                              Comprobante de pago de {i.nombres}
                            </span>
                            <div className="flex items-center gap-2">
                              <a
                                href={i.comprobanteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 rounded-lg bg-foreground-200 text-foreground-800 text-xs font-semibold hover:bg-foreground-300"
                              >
                                Abrir en pestaña
                              </a>
                              <button
                                onClick={() => setFilaAbierta(null)}
                                className="px-3 py-1 rounded-lg bg-foreground-200 text-foreground-800 text-xs font-semibold hover:bg-foreground-300"
                              >
                                Cerrar ✕
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-center">
                            <img
                              src={i.comprobanteUrl}
                              alt={`Comprobante de ${i.nombres}`}
                              className="max-w-full max-h-[60vh] rounded-lg border border-foreground-200"
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de eliminación con contraseña */}
      {aEliminar && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50"
          onClick={() => !eliminando && setAEliminar(null)}
        >
          <form
            onSubmit={confirmarEliminar}
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-4">
              <i className="ri-delete-bin-line text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-center text-foreground-950">Eliminar inscripción</h3>
            <p className="text-sm text-foreground-600 text-center mt-1">
              Vas a eliminar a <strong>{aEliminar.nombres}</strong> (cédula {aEliminar.cedula}).
              Esta acción <strong>no se puede deshacer</strong>.
            </p>

            <label className="block text-sm font-semibold text-foreground-800 mt-5 mb-1">
              Ingresa tu contraseña para confirmar
            </label>
            <input
              type="password"
              autoFocus
              value={passEliminar}
              onChange={(e) => setPassEliminar(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-foreground-200 focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            {errorEliminar && <p className="mt-2 text-sm text-red-600">{errorEliminar}</p>}

            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => setAEliminar(null)}
                disabled={eliminando}
                className="flex-1 px-4 py-2.5 rounded-full bg-foreground-200 text-foreground-800 font-semibold hover:bg-foreground-300 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={eliminando || !passEliminar}
                className="flex-1 px-4 py-2.5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60"
              >
                {eliminando ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
